import { useCallback } from 'react';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData
} from '@tanstack/react-query';
import {
    addCommunicationEntry,
    createCommunication,
    deleteCommunication,
    deleteCommunicationEntry,
    fetchCommunication,
    fetchCommunications,
    updateCommunication,
    updateCommunicationEntry,
    type CommunicationsListParams,
    type CreateCommunicationInput,
    type CreateEntryInput,
    type UpdateCommunicationInput,
    type UpdateEntryInput
} from '../services/communications';
import {
    COMMUNICATION_DETAIL_KEY,
    COMMUNICATIONS_KEY,
    PRACTICE_DETAIL_KEY,
    PRACTICE_TIMELINE_KEY
} from './queryKeys';

// Keeps a thread's practiceId-owning caches (callCount badge, timeline
// feed) fresh after any thread/entry mutation. No-op for practice-less threads.
function invalidatePracticeCaches(
    queryClient: ReturnType<typeof useQueryClient>,
    practiceId: string | null | undefined
) {
    if (!practiceId) return;
    queryClient.invalidateQueries({ queryKey: PRACTICE_DETAIL_KEY(practiceId) });
    queryClient.invalidateQueries({ queryKey: PRACTICE_TIMELINE_KEY(practiceId) });
}

export function useCommunications(params: CommunicationsListParams & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;
    const queryClient = useQueryClient();
    const queryKey = [
        COMMUNICATIONS_KEY,
        'list',
        rest.page,
        rest.limit,
        rest.practice ?? '',
        rest.status ?? '',
        rest.category ?? '',
        rest.assignee ?? '',
        rest.patient ?? '',
        rest.channel ?? '',
        rest.search ?? '',
        rest.sort ?? '',
        rest.order ?? ''
    ];

    const { data, isLoading, isFetching, isError, error } = useQuery({
        queryKey,
        queryFn: () => fetchCommunications(rest),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY] });
    }, [queryClient]);

    return {
        communications: data?.data ?? [],
        meta: data?.meta ?? { total: 0, page: rest.page, limit: rest.limit, totalPages: 1 },
        isLoading,
        isFetching,
        isError,
        error,
        refresh
    };
}

/** Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'`
 *  mode. Query key nests under `COMMUNICATIONS_KEY` so mutations' blanket
 *  invalidation already catches it. */
export function useInfiniteCommunications(
    params: Omit<CommunicationsListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isError,
        error,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery({
        queryKey: [
            COMMUNICATIONS_KEY,
            'infinite',
            rest.limit,
            rest.practice ?? '',
            rest.status ?? '',
            rest.category ?? '',
            rest.assignee ?? '',
            rest.patient ?? '',
            rest.channel ?? '',
            rest.search ?? '',
            rest.sort ?? '',
            rest.order ?? ''
        ],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchCommunications({ ...rest, page: pageParam as number }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                ? lastPage.meta.page + 1
                : undefined,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        communications: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { total: 0, page: 1, limit: rest.limit, totalPages: 1 },
        isLoading,
        isFetching,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

// Backs the thread detail page - a direct URL visit or refresh needs its
// own fetch (the list's cached row data doesn't cover that).
export function useCommunication(id: string | undefined) {
    const query = useQuery({
        queryKey: COMMUNICATION_DETAIL_KEY(id ?? ''),
        queryFn: () => fetchCommunication(id as string),
        enabled: Boolean(id),
        staleTime: 60_000
    });

    return {
        communication: query.data ?? null,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch
    };
}

export function useCreateCommunication() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateCommunicationInput) => createCommunication(input),
        onSuccess: (created) => {
            queryClient.setQueryData(COMMUNICATION_DETAIL_KEY(created.id), created);
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY] });
            invalidatePracticeCaches(queryClient, created.practiceId);
        }
    });
}

/** Thread-field-only patch (title, status, categories, assignee, patients, practice). */
export function useUpdateCommunication() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: UpdateCommunicationInput }) =>
            updateCommunication(id, patch),
        onSuccess: (updated) => {
            queryClient.setQueryData(COMMUNICATION_DETAIL_KEY(updated.id), updated);
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY] });
            invalidatePracticeCaches(queryClient, updated.practiceId);
        }
    });
}

/** `practiceId` only feeds cache invalidation - the wire call still just sends `id`. */
export function useDeleteCommunication() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string; practiceId: string | null }) => deleteCommunication(id),
        onSuccess: (_data, { id, practiceId }) => {
            queryClient.removeQueries({ queryKey: COMMUNICATION_DETAIL_KEY(id) });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY] });
            invalidatePracticeCaches(queryClient, practiceId);
        }
    });
}

/** Every entry mutation returns the full refreshed detail - the timeline updates from the response, not a refetch. */
export function useAddCommunicationEntry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: CreateEntryInput }) =>
            addCommunicationEntry(id, input),
        onSuccess: (detail) => {
            queryClient.setQueryData(COMMUNICATION_DETAIL_KEY(detail.id), detail);
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'list'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'infinite'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'summary'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'insights'] });
            invalidatePracticeCaches(queryClient, detail.practiceId);
        }
    });
}

export function useUpdateCommunicationEntry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            id,
            entryId,
            patch
        }: {
            id: string;
            entryId: string;
            patch: UpdateEntryInput;
        }) => updateCommunicationEntry(id, entryId, patch),
        onSuccess: (detail) => {
            queryClient.setQueryData(COMMUNICATION_DETAIL_KEY(detail.id), detail);
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'list'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'infinite'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'summary'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'insights'] });
            invalidatePracticeCaches(queryClient, detail.practiceId);
        }
    });
}

/** The server rejects deleting a thread's last remaining entry (400) - callers surface `error.message` via apiErrorMessage. */
export function useDeleteCommunicationEntry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, entryId }: { id: string; entryId: string }) =>
            deleteCommunicationEntry(id, entryId).then(() => fetchCommunication(id)),
        onSuccess: (detail) => {
            queryClient.setQueryData(COMMUNICATION_DETAIL_KEY(detail.id), detail);
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'list'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'infinite'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'summary'] });
            queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_KEY, 'insights'] });
            invalidatePracticeCaches(queryClient, detail.practiceId);
        }
    });
}
